import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import { motion } from "framer-motion";
import { FaCoins } from "react-icons/fa";
import { loadStripe } from "@stripe/stripe-js";
import PaymentForms from "./PaymentForms";

const CoinPurchase = () => {
  const { fetchWithRetry, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [coinPackages, setCoinPackages] = useState([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const [coinTransactions, setCoinTransactions] = useState([]);
  const [globalPaymentHistory, setGlobalPaymentHistory] = useState([]);
  const [localPaymentHistory, setLocalPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCoinPackage, setSelectedCoinPackage] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentGateways, setPaymentGateways] = useState({
    stripe: { enabled: false },
    paypal_api: { enabled: false },
    paypal_me: { enabled: false },
    coinpayments: { enabled: false },
    coinbase: { enabled: false },
    nowpayments: { enabled: false },
    binancepay: { enabled: false },
  });
  const [localPaymentGateways, setLocalPaymentGateways] = useState({
    momo: { enabled: false },
    zalopay: { enabled: false },
    vnpay: { enabled: false },
    vietqr: { enabled: false },
  });
  const [stripePromise, setStripePromise] = useState(null);

  const fetchCoinBalance = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-balance" });
      setCoinBalance(res.data.coinBalance || 0);
    } catch (error) {
      console.error("Lỗi khi lấy số dư coin:", error);
      setError("Không thể lấy số dư coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchCoinPackages = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coins" });
      setCoinPackages(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách gói coin:", error);
      setError("Không thể lấy danh sách gói coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchCoinTransactions = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-transactions" });
      setCoinTransactions(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy lịch sử giao dịch coin:", error);
      setError("Không thể lấy lịch sử giao dịch coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  useEffect(() => {
    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "coin_purchase") {
        setNotificationMessage(`Bạn đã mua thành công ${notification.message}!`);
        fetchCoinBalance();
        fetchCoinTransactions();
        setTimeout(() => {
          setNotificationMessage(null);
          setSelectedCoinPackage(null);
          setPaymentMethod(null);
        }, 3000);
        

      } else if (notification.type === "payment_pending") {
        setNotificationMessage(notification.message || "Giao dịch của bạn đã được ghi nhận và đang chờ xác nhận!");
      } else if (notification.type === "payment_failed") {
        setNotificationMessage(notification.message || "Giao dịch thất bại. Vui lòng thử lại!");
      } else if (notification.type === "payment_refunded") {
        setNotificationMessage(notification.message || "Giao dịch của bạn đã được hoàn tiền!");
        fetchCoinBalance();
        fetchCoinTransactions();
        setTimeout(() => {
          setNotificationMessage(null);
        }, 3000);
      }
    });
    // Thêm sự kiện để cập nhật số dư coin
    socket.on("coinBalanceUpdate", (data) => {
      setCoinBalance(data.coinBalance);
      setNotificationMessage(`Số dư coin của bạn đã được cập nhật: ${data.coinBalance} coin`);
      fetchCoinTransactions(); // Làm mới lịch sử giao dịch
    });


    socket.on("error", (error) => {
      setError(error.message || "Lỗi kết nối WebSocket!");
    });

    return () => {
      socket.off("notification");
      socket.off("coinBalanceUpdate"); // Gỡ sự kiện khi component unmount
      socket.off("error");
    };
  }, [fetchCoinBalance, fetchCoinTransactions]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setError("Vui lòng đăng nhập để truy cập trang này!");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Lấy lịch sử giao dịch quốc tế
        try {
          const globalPaymentRes = await fetchWithRetry({ method: "get", url: "/api/payments/coin-payment-history" });
          setGlobalPaymentHistory(globalPaymentRes.data.payments || []);
        } catch (error) {
          console.error("Lỗi khi lấy lịch sử giao dịch quốc tế:", error);
          setError("Không thể lấy lịch sử giao dịch quốc tế. Vui lòng thử lại sau!");
        }

                // Lấy lịch sử giao dịch nội địa
                try {
                  const localPaymentRes = await fetchWithRetry({ method: "get", url: "/api/local-payments/local-coin-payment-history" });
                  setLocalPaymentHistory(localPaymentRes.data.payments || []);
                } catch (error) {
                  console.error("Lỗi khi lấy lịch sử giao dịch nội địa:", error);
                  setError("Không thể lấy lịch sử giao dịch nội địa. Vui lòng thử lại sau!");
                }
        
                await fetchCoinBalance();
                await fetchCoinPackages();
                await fetchCoinTransactions();
        
                const gatewaysRes = await fetchWithRetry({ method: "get", url: "/api/users/public-payment-gateways" });
                const gatewaysData = gatewaysRes.data;
                setPaymentGateways(gatewaysData);
        
                const localGatewaysRes = await fetchWithRetry({ method: "get", url: "/api/local-payments/public-local-payment-gateways" });
                const localGatewaysData = localGatewaysRes.data;
                setLocalPaymentGateways(localGatewaysData);
        
                if (!gatewaysData) {
                  setError("Không nhận được dữ liệu cổng thanh toán từ server!");
                } else {
                  if (
                    (!gatewaysData.stripe || !gatewaysData.stripe.enabled) &&
                    (!gatewaysData.paypal_api || !gatewaysData.paypal_api.enabled) &&
                    (!gatewaysData.paypal_me || !gatewaysData.paypal_me.enabled) &&
                    (!gatewaysData.coinpayments || !gatewaysData.coinpayments.enabled) &&
                    (!gatewaysData.coinbase || !gatewaysData.coinbase.enabled) &&
                    (!gatewaysData.nowpayments || !gatewaysData.nowpayments.enabled) &&
                    (!gatewaysData.binancepay || !gatewaysData.binancepay.enabled) &&
                    (!localGatewaysData.momo || !localGatewaysData.momo.enabled) &&
                    (!localGatewaysData.zalopay || !localGatewaysData.zalopay.enabled) &&
                    (!localGatewaysData.vnpay || !localGatewaysData.vnpay.enabled) &&
                    (!localGatewaysData.vietqr || !localGatewaysData.vietqr.enabled)
                  ) {
                    setError("Không có cổng thanh toán nào được kích hoạt. Vui lòng liên hệ quản trị viên!");
                  } else if (gatewaysData.paypal_me?.enabled && !gatewaysData.paypal_me.username) {
                    setError("Cấu hình PayPal.me thiếu username. Vui lòng liên hệ quản trị viên!");
                  } else if (gatewaysData.coinpayments?.enabled && !gatewaysData.coinpayments.merchantId) {
                    setError("Cấu hình CoinPayments thiếu Merchant ID. Vui lòng liên hệ quản trị viên!");
                  } else if (gatewaysData.stripe?.enabled && gatewaysData.stripe.publishableKey) {
                    try {
                      const stripe = await loadStripe(gatewaysData.stripe.publishableKey);
                      setStripePromise(stripe);
                    } catch (err) {
                      setError("Không thể khởi tạo Stripe. Vui lòng thử lại sau.");
                      console.error("Lỗi khi khởi tạo Stripe:", err);
                    }
                  }
                }
              } catch (error) {
                if (error.message.includes("500")) {
                  setError("Lỗi server khi tải dữ liệu. Vui lòng thử lại sau!");
                } else if (error.message.includes("401")) {
                  setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
                } else {
                  setError(error.message || "Không thể tải dữ liệu! Vui lòng thử lại sau.");
                }
                console.error("Lỗi khi lấy dữ liệu:", error);
              } finally {
                setLoading(false);
              }
            };
        
            if (!authLoading) {
              fetchData();
            }
          }, [fetchWithRetry, authLoading, user, fetchCoinBalance, fetchCoinPackages, fetchCoinTransactions]);
        
          const handlePaymentSuccess = useCallback(() => {
            setNotificationMessage("Mua coin thành công!");
            fetchCoinBalance();
            fetchCoinTransactions();
            setTimeout(() => {
              setNotificationMessage(null);
              setSelectedCoinPackage(null);
              setPaymentMethod(null);
            }, 3000);
          }, [fetchCoinBalance, fetchCoinTransactions]);
        
          const handlePaymentPending = useCallback((data) => {
            const message = data && data.message ? data.message : "Giao dịch của bạn đã được ghi nhận và đang chờ xác nhận!";
            setNotificationMessage(message);
            fetchCoinBalance();
            fetchCoinTransactions();
          }, [fetchCoinBalance, fetchCoinTransactions]);
        
          if (loading || authLoading) {
            return <p className="text-center">Đang tải...</p>;
          }
        
          return (
            <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
              <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">Mua Coin</h2>
        
              {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
                  <span>{error}</span>
                  {error.includes("Phiên làm việc hết hạn") ? (
                    <button
                      onClick={() => navigate("/login")}
                      className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
                    >
                      Đăng nhập lại
                    </button>
                  ) : (
                    <button
                      onClick={() => setError(null)}
                      className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
                    >
                      Đóng
                    </button>
                  )}
                </div>
              )}
        
              {notificationMessage && (
                <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
              )}
        
              {/* Số dư coin */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Số dư Coin của bạn</h3>
                <div className="p-6 bg-white rounded-xl shadow-lg flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <FaCoins className="text-yellow-500 w-8 h-8" />
                    <p className="text-gray-700 text-lg">
                      <strong>Số dư:</strong> {coinBalance} Coin
                    </p>
                  </div>
                  <button
                    onClick={() => navigate("/upgrade-account")}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                  >
                    Nâng cấp tài khoản
                  </button>
                </div>
              </div>
        
              {/* Danh sách gói coin */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Danh sách gói Coin</h3>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                  {coinPackages.length > 0 ? (
                    coinPackages.map((pkg) => (
                      <div
                        key={pkg._id}
                        className={`p-6 bg-white rounded-xl shadow-lg cursor-pointer ${
                          selectedCoinPackage?._id === pkg._id ? "border-2 border-blue-500" : ""
                        }`}
                        onClick={() => setSelectedCoinPackage(pkg)}
                      >
                        <div className="flex justify-center mb-4">
                          <FaCoins className="text-yellow-500 w-12 h-12" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">{pkg.name}</h3>
                        <p className="text-2xl font-bold text-gray-800 mb-2 text-center">{pkg.coinAmount} Coin</p>
                        <p className="text-lg text-gray-600 mb-2 text-center">{pkg.price.toLocaleString()} VND</p>
                        {pkg.description && <p className="text-sm text-gray-600 mb-4 text-center">{pkg.description}</p>}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCoinPackage(pkg);
                          }}
                          className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-200"
                        >
                          Mua ngay
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg col-span-3">
                      Hiện tại không có gói coin nào khả dụng. Vui lòng liên hệ quản trị viên để biết thêm chi tiết.
                    </p>
                  )}
                </motion.div>
              </div>
        
              {/* Thanh toán mua coin */}
              {selectedCoinPackage && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Thanh toán gói {selectedCoinPackage.name}</h3>
                  <PaymentForms
                    plan={selectedCoinPackage}
                    packageId={selectedCoinPackage._id}
                    isCoinPurchase={true}
                    onSuccess={handlePaymentSuccess}
                    onPending={handlePaymentPending}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paymentGateways={paymentGateways}
                    localPaymentGateways={localPaymentGateways}
                    fetchWithRetry={fetchWithRetry}
                    user={user}
                    stripePromise={stripePromise}
                  />
                </div>
              )}
        
              Lịch sử giao dịch coin
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Lịch sử giao dịch Coin</h3>
                {coinTransactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg shadow-lg">
                      <thead>
                        <tr className="bg-blue-500 text-white">
                          <th className="p-4 text-left">Loại giao dịch</th>
                          <th className="p-4 text-left">Số Coin</th>
                          <th className="p-4 text-left">Mô tả</th>
                          <th className="p-4 text-left">Ngày giao dịch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coinTransactions.map((transaction) => (
                          <tr key={transaction._id} className="border-b hover:bg-gray-50 transition duration-200">
                            <td className="p-4 text-gray-700">
                              {transaction.type === "purchase"
                                ? "Mua Coin"
                                : transaction.type === "refund"
                                ? "Hoàn tiền"
                                : transaction.type === "gift_sent"
                                ? "Gửi quà"
                                : transaction.type === "gift_received"
                                ? "Nhận quà"
                                : transaction.type === "membership_upgrade"
                                ? "Nâng cấp VIP"
                                : "Không xác định"}
                            </td>
                            <td className={`p-4 ${transaction.coinAmount >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {transaction.coinAmount > 0 ? "+" : ""}
                              {transaction.coinAmount} Coin
                            </td>
                            <td className="p-4 text-gray-700">{transaction.description}</td>
                            <td className="p-4 text-gray-700">{new Date(transaction.createdAt).toLocaleString("vi-VN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
                    Bạn chưa có giao dịch Coin nào.
                  </p>
                )}
              </div>
        
              {/* Lịch sử thanh toán */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Lịch sử thanh toán mua Coin</h3>
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Giao dịch quốc tế</h4>
                  {globalPaymentHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg shadow-lg">
                        <thead>
                          <tr className="bg-blue-500 text-white">
                            <th className="p-4 text-left">Gói</th>
                            <th className="p-4 text-left">Số tiền (VND)</th>
                            <th className="p-4 text-left">Phương thức</th>
                            <th className="p-4 text-left">Trạng thái</th>
                            <th className="p-4 text-left">Ngày giao dịch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {globalPaymentHistory.map((payment) => (
                            <tr key={payment._id} className="border-b hover:bg-gray-50 transition duration-200">
                              <td className="p-4 text-gray-700">{payment.planId?.name || "N/A"}</td>
                              <td className="p-4 text-gray-700">{payment.amount.toLocaleString()}</td>
                              <td className="p-4 text-gray-700">
                                {payment.paymentMethod === "stripe"
                                  ? "Stripe"
                                  : payment.paymentMethod === "paypal_api"
                                  ? "PayPal API"
                                  : payment.paymentMethod === "paypal_me"
                                  ? "PayPal.me"
                                  : payment.paymentMethod === "coinpayments"
                                  ? "CoinPayments (Tiền điện tử)"
                                  : payment.paymentMethod === "coinbase"
                                  ? "Coinbase Commerce (Tiền điện tử)"
                                  : payment.paymentMethod === "nowpayments"
                                  ? "NOWPayments (Tiền điện tử)"
                                  : payment.paymentMethod === "binancepay"
                                  ? "Binance Pay (Tiền điện tử)"
                                  : payment.paymentMethod}
                              </td>
                              <td className="p-4 text-gray-700">
                                {payment.status === "completed"
                                  ? "Hoàn tất"
                                  : payment.status === "pending"
                                  ? "Đang chờ"
                                  : payment.status === "pending_verification"
                                  ? "Đang chờ xác minh"
                                  : payment.status === "refunded"
                                  ? "Đã hoàn tiền"
                                  : "Thất bại"}
                              </td>
                              <td className="p-4 text-gray-700">{new Date(payment.transactionDate).toLocaleString("vi-VN")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
                      Bạn chưa có giao dịch quốc tế nào.
                    </p>
                  )}
                </div>
        
                <div>
                  <h4 className="text-md font-medium text-gray-700 mb-2">Giao dịch nội địa</h4>
                  {localPaymentHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg shadow-lg">
                        <thead>
                          <tr className="bg-blue-500 text-white">
                            <th className="p-4 text-left">Gói</th>
                            <th className="p-4 text-left">Số tiền (VND)</th>
                            <th className="p-4 text-left">Phương thức</th>
                            <th className="p-4 text-left">Trạng thái</th>
                            <th className="p-4 text-left">Ngày giao dịch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {localPaymentHistory.map((payment) => (
                            <tr key={payment._id} className="border-b hover:bg-gray-50 transition duration-200">
                              <td className="p-4 text-gray-700">{payment.planId?.name || "N/A"}</td>
                              <td className="p-4 text-gray-700">{payment.amount.toLocaleString()}</td>
                              <td className="p-4 text-gray-700">
                                {payment.paymentMethod === "momo"
                                  ? "MoMo"
                                  : payment.paymentMethod === "zalopay"
                                  ? "ZaloPay"
                                  : payment.paymentMethod === "vnpay"
                                  ? "VNPay"
                                  : payment.paymentMethod === "vietqr"
                                  ? "VietQR"
                                  : payment.paymentMethod}
                              </td>
                              <td className="p-4 text-gray-700">
                                {payment.status === "completed"
                                  ? "Hoàn tất"
                                  : payment.status === "pending"
                                  ? "Đang chờ"
                                  : payment.status === "pending_verification"
                                  ? "Đang chờ xác minh"
                                  : payment.status === "refunded"
                                  ? "Đã hoàn tiền"
                                  : "Thất bại"}
                              </td>
                              <td className="p-4 text-gray-700">{new Date(payment.transactionDate).toLocaleString("vi-VN")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
                      Bạn chưa có giao dịch nội địa nào.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        };
        
        export default CoinPurchase;